import React, { useEffect } from 'react';
import styled from 'styled-components';
import { IoArrowDown, IoArrowUp } from 'react-icons/io5';
import { useStickToBottomContext, StickToBottom } from 'use-stick-to-bottom';
import { useSignalEffect } from '@preact/signals-react';
import { UserMessage } from '@/features/ai-agents/components/user-message';
import { AiChatMessage } from '@/features/ai-agents/shared/models';
import { useChatSession } from '@/features/ai-agents/chat/context';
import { AgentMessage } from '@/features/ai-agents/components/agent-message';
import { VSpace } from '@/components/spacing';
import { breakpoints } from '@/components/responsive';

export interface ChatMessagesProps {
  showReasoning?: boolean;
  showTools?: boolean;

  isLocalHost?: boolean;
}

export function ChatMessages({ showTools, showReasoning }: ChatMessagesProps) {
  const { messages, processedEvents, isProcessing } = useChatSession();

  return (
    <Wrapper>
      <StyledStickToBottom resize="smooth" initial="smooth">
        {/* @ts-ignore */}
        <StickToBottom.Content className="content">
          <MessagesWrapper>
            <VSpace value={0.1} />
            {messages?.value?.map((msg, index) => {
              const isLatestAndProcessing =
                isProcessing.value && index === messages.value.length - 1;
              return msg.role === `user` ? (
                <UserMessage key={messageKey(msg, index)} message={msg} />
              ) : (
                <AgentMessage
                  key={messageKey(msg, index)}
                  message={msg}
                  showReasoning={showReasoning}
                  showTools={showTools}
                  isProcessing={isProcessing}
                  processedEvents={
                    isLatestAndProcessing ? processedEvents : undefined
                  }
                />
              );
            })}
            <VSpace value={2} />
          </MessagesWrapper>
        </StickToBottom.Content>
        <ScrollToButtons />
      </StyledStickToBottom>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
`;

const StyledStickToBottom = styled(StickToBottom)`
  && {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr;

    height: 34vh;

    ${breakpoints.upH(400)} {
      height: 41vh;
    }

    ${breakpoints.upH(600)} {
      height: 46vh;
    }

    ${breakpoints.upH(650)} {
      height: 51vh;
    }

    ${breakpoints.upH(700)} {
      height: 57vh;
    }

    ${breakpoints.upH(750)} {
      height: 63vh;
    }

    ${breakpoints.upH(800)} {
      height: 65vh;
    }

    ${breakpoints.upH(850)} {
      height: 66vh;
    }

    ${breakpoints.upH(900)} {
      height: 70vh;
    }

    ${breakpoints.upH(950)} {
      height: 72vh;
    }

    ${breakpoints.upH(1000)} {
      height: 74vh;
    }

    ${breakpoints.upH(1050)} {
      height: 76vh;
    }

    ${breakpoints.upH(1100)} {
      height: 78vh;
    }

    ${breakpoints.upH(1150)} {
      height: 79vh;
    }

    ${breakpoints.upH(1200)} {
      height: 80vh;
    }

    ${breakpoints.upH(1250)} {
      height: 82vh;
    }

    ${breakpoints.upH(1300)} {
      height: 83vh;
    }

    ${breakpoints.upH(1350)} {
      height: 84vh;
    }

    ${breakpoints.upH(1400)} {
      height: 86vh;
    }

    mask-image: linear-gradient(
      var(--mask-direction, to bottom),
      hsl(0 0% 0% / 0),
      rgb(248 249 251 / 1) 5%,
      rgb(248 249 251 / 1) 85%,
      hsl(0 0% 0% / 0)
    );

    .content {
    }
  }
`;

export function messageKey(message: AiChatMessage, index: number): string {
  return `${message.role}-${message.created_at}-${index}`;
}

const MessagesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4rem;
  width: 100%;
  height: auto;
`;

function ScrollToButtons() {
  const { isAtBottom, scrollToBottom, scrollRef } = useStickToBottomContext();
  const { isProcessing } = useChatSession();

  const [isAtTop, setIsAtTop] = React.useState(false);

  useEffect(() => {
    if (!scrollRef.current) return;

    const handleScroll = () => {
      if (!scrollRef.current) return;
      setIsAtTop(scrollRef.current.scrollTop === 0);
    };
    scrollRef.current.addEventListener(`scroll`, handleScroll);

    // eslint-disable-next-line consistent-return
    return () => {
      if (!scrollRef.current) return;
      scrollRef.current.removeEventListener(`scroll`, handleScroll);
    };
  }, [scrollRef]);

  useSignalEffect(() => {
    if (isProcessing.value) {
      scrollToBottom();
    }
  });

  const scrollToPrevUserMessage = () => {
    if (!scrollRef.current) return;

    const container = scrollRef.current;
    const userMessages = Array.from(
      container.querySelectorAll(`.ai-user-message`),
    ) as HTMLElement[];

    const containerRect = container.getBoundingClientRect();
    const viewportCenter = containerRect.top + containerRect.height / 2;

    const targetMessage = userMessages.reverse().find((msg) => {
      const msgRect = msg.getBoundingClientRect();
      const msgCenter = msgRect.top + msgRect.height / 2;

      return msgCenter < viewportCenter - 20;
    });

    if (targetMessage) {
      targetMessage.scrollIntoView({
        behavior: `smooth`,
        block: `center`,
      });
    }
  };

  const scrollToNextUserMessageOrBottom = () => {
    if (!scrollRef.current) return;

    const container = scrollRef.current;
    const userMessages = Array.from(
      container.querySelectorAll(`.ai-user-message`),
    ) as HTMLElement[];

    const containerRect = container.getBoundingClientRect();
    const viewportCenter = containerRect.top + containerRect.height / 2;

    const targetMessage = userMessages.find((msg) => {
      const msgRect = msg.getBoundingClientRect();
      const msgCenter = msgRect.top + msgRect.height / 2;

      return msgCenter > viewportCenter + 20;
    });

    if (targetMessage) {
      targetMessage.scrollIntoView({
        behavior: `smooth`,
        block: `center`,
      });
    } else {
      scrollToBottom();
    }
  };

  return (
    <ScrollToWrapper>
      <ScrollToBottomButton
        onClick={scrollToNextUserMessageOrBottom}
        visible={!isAtBottom}
        aria-label="Scroll to bottom"
      >
        <IoArrowDown size={20} />
      </ScrollToBottomButton>

      {!isAtTop && (
        <ScrollToBottomButton
          onClick={scrollToPrevUserMessage}
          visible={!isAtBottom}
          aria-label="Scroll to bottom"
        >
          <IoArrowUp size={20} />
        </ScrollToBottomButton>
      )}
    </ScrollToWrapper>
  );
}

const ScrollToWrapper = styled.div`
  position: absolute;
  bottom: 14rem;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: row;
  gap: 10px;
  z-index: 10;
`;

const ScrollToBottomButton = styled.button<{ visible: boolean }>`
  outline: none;
  border: none;
  appearance: none;

  background-color: white;
  box-shadow: 0 2px 10px rgb(0 0 0 / 0.1), 0 8px 10px rgb(0 0 0 / 0.06);
  border-radius: 100%;

  height: 3.5rem;
  width: 3.5rem;

  display: flex;
  align-items: center;
  justify-content: center;

  cursor: pointer;

  transition: opacity 0.3s, transform 0.3s;
  opacity: ${(p) => (p.visible ? 1 : 0)};
  pointer-events: ${(p) => (p.visible ? `auto` : `none`)};
  transform: translateY(${(p) => (p.visible ? `0` : `40px`)});
`;
